import React, {PureComponent} from 'react';
import Parser from '../../utils/SubtitlesParser';
import './styles/subtitles.css';
import Metadata from '../../requests/apa/Metadata';
import Utils from '../../utils/Utils';
import Device from "../../devices/device";

class VirtualSubtitles extends PureComponent {

  constructor(props) {
    super(props);
    // No parsear en el constructor — diferir al componentDidMount
    // para no bloquear el hilo principal durante la reconciliación de React
    this.subtitleArray = [];
    this.handleChange = this.handleChange.bind(this);
    this.handleScreenPosition = this.handleScreenPosition.bind(this);
    this.flag = 0;

    // Throttle: evitar procesamiento en cada tick de progreso (4-25x/segundo en PS4)
    this._throttleMs = 100;
    this._lastHandledTime = -1;
    this._lastSubId = null; // Para evitar repintados innecesarios

    // Callback refs en lugar de string ref y arrow ref inline
    this._wrapperRef = null;
    this._containerRef = null;
    this._setWrapperRef = (el) => {
      this._wrapperRef = el;
    };
    this._setContainerRef = (el) => {
      this._containerRef = el;
    };

    // Control de ciclo de vida para el parseo asíncrono
    this._isMounted = false;
    // Contador de versión: si llega un nuevo subtitle antes de que termine
    // el parseo anterior, solo se procesa el más reciente
    this._parseVersion = 0;
    // ID del requestAnimationFrame pendiente; permite cancelarlo si llega
    // un subtítulo nuevo antes de que el frame anterior haya pintado.
    this._pendingRaf = null;
  }

  // Construye el string CSS de los estilos del subtítulo (se pre-computa una vez al parsear)
  // NOTA: ya no se usa en el rAF (ver _applySubtitleStyles). Se conserva por si hay
  // alguna referencia externa, pero el código de render no llama a style.cssText.
  _buildCssText(styles) {
    if (!styles) return '';
    const keys = Object.keys(styles);
    let css = '';
    for (let i = 0; i < keys.length; i++) {
      // 'line' es un atributo de posicionamiento WebVTT, no una propiedad CSS válida.
      // Escribirlo en style.cssText puede crashear el CSS parser de WebKit antiguo (PS4).
      if (keys[i] === 'line') continue;
      css += keys[i] + ':' + styles[keys[i]] + ';';
    }
    return css;
  }

  // Escribe el contenido del subtítulo en el contenedor sin usar innerHTML.
  // innerHTML con HTML arbitrario puede crashear el parser HTML del WebKit viejo del PS4
  // cuando el nodo fue insertado en el mismo event-loop tick (ej. tras forceUpdate).
  // Se usan createTextNode + createElement('br') que son operaciones DOM seguras.
  _applySubtitleContent(container, htmlContent) {
    // Vaciar el contenedor de forma segura
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    // Partir por saltos de línea (<br> / <br/>) y reconstruir con nodos reales
    var segments = htmlContent.replace(/<br\s*\/?>/gi, '\x00').split('\x00');
    for (var i = 0; i < segments.length; i++) {
      if (i > 0) {
        container.appendChild(document.createElement('br'));
      }
      // Eliminar cualquier otro tag HTML y usar texto plano
      var text = segments[i].replace(/<[^>]+>/g, '');
      if (text) {
        container.appendChild(document.createTextNode(text));
      }
    }
  }

  // Aplica estilos de posicionamiento uno a uno en lugar de usar style.cssText.
  // style.cssText con keys camelCase (ej. "textAlign:center") no es CSS válido —
  // son nombres de propiedades JS DOM, no CSS. Los browsers modernos lo ignoran;
  // el WebKit del PS4 puede crashear con propiedades desconocidas en cssText.
  _applySubtitleStyles(domStyle, styles) {
    // Limpiar propiedades de posición antes de aplicar las nuevas
    domStyle.top = '';
    domStyle.bottom = '';
    domStyle.left = '';
    domStyle.textAlign = '';
    domStyle.width = '';
    if (!styles) return;
    var keys = Object.keys(styles);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      // 'line' es WebVTT-only, no existe como propiedad DOM de style
      if (key === 'line') continue;
      domStyle[key] = styles[key];
    }
  }

  handleScreenPosition(e) {
    if (this._wrapperRef) {
      if (e.detail.isShowed) {
        if (!this._wrapperRef.classList.contains(e.detail.className)) {
          this._wrapperRef.classList.add(e.detail.className);
        }
      } else {
        if (this._wrapperRef.classList.contains(e.detail.className)) {
          this._wrapperRef.classList.remove(e.detail.className);
        }
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const subtitleChanged = this.props.subtitle !== nextProps.subtitle;
    const classChanged = this.props.className !== nextProps.className;

    if (subtitleChanged) {
      if (nextProps.subtitle) {
        // Diferir él parseó: no bloquear el hilo principal durante SCU
        this._scheduleParse(nextProps.subtitle);
        // No re-renderizar todavía; forceUpdate lo hará tras el parseo
      } else {
        // Subtítulos desactivados — limpiar de inmediato
        this.subtitleArray = [];
        this._lastHandledTime = -1;
        // Resetear el dedup para que el primer subtítulo de la nueva pista
        // no sea bloqueado por un start-time coincidente con el idioma anterior.
        this._lastSubId = null;
      }
    }

    // Re-renderizar si: className cambió, o subtítulos fueron desactivados
    return classChanged || (subtitleChanged && !nextProps.subtitle);
  }

  // Programa él parseó en el siguiente tick para liberar el hilo principal
  _scheduleParse(subtitleText) {
    this._parseVersion++;
    const version = this._parseVersion;
    const self = this;
    setTimeout(function () {
      if (!self._isMounted || self._parseVersion !== version) return;
      self._parseSubtitles(subtitleText);
    }, 0);
  }

  // Parseo real: se ejecuta fuera del ciclo de render de React
  _parseSubtitles(subtitleText) {
    const lag_range = parseInt(Metadata.get('lag_range', '0'));
    const platform = Device.getDevice().getPlatform();
    this.flag = Utils.getByDeviceApaTimeSubtitle(platform);

    const subtitlesData = Parser.convertVttToArray(subtitleText);

    if (lag_range
      && subtitlesData
      && subtitlesData.length > 0
      && typeof subtitlesData[0].start === 'number'
      && subtitlesData[0].start > lag_range) {
      const len = subtitlesData.length;
      const adjusted = new Array(len);
      for (let i = 0; i < len; i++) {
        adjusted[i] = {
          start: subtitlesData[i].start - lag_range,
          end: subtitlesData[i].end - lag_range,
          part: subtitlesData[i].part,
          // Almacenar el objeto styles directamente en lugar de pre-computar cssText.
          // style.cssText con propiedades camelCase (ej. "textAlign:center") no es CSS
          // válido y puede crashear el parser de WebKit antiguo del PS4.
          styles: subtitlesData[i].styles,
        };
      }
      this.subtitleArray = adjusted;
    } else {
      this.subtitleArray = subtitlesData || [];
    }

    // Reiniciar throttle y dedup al cambiar la pista de subtítulos.
    // _lastSubId debe resetearse aquí: si el primer subtítulo del nuevo idioma
    // tiene el mismo start-time que el último mostrado del idioma anterior,
    // sin este reset el dedup lo bloquearía y nunca se pintaría.
    this._lastHandledTime = -1;
    this._lastSubId = null;

    // Forzar re-render ahora que el array está listo (bypasea shouldComponentUpdate)
    if (this._isMounted) {
      this.forceUpdate();
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.setHandleChangeSubtitle(this.handleChange);
    document.addEventListener('handlesubtitlesscreenposition', this.handleScreenPosition);

    // Parsear el subtitle inicial si ya llega con datos en el primer montaje
    if (this.props.subtitle) {
      this._scheduleParse(this.props.subtitle);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    // Cancelar rAF pendiente para no escribir en un nodo ya desmontado
    if (this._pendingRaf !== null) {
      window.cancelAnimationFrame(this._pendingRaf);
      this._pendingRaf = null;
    }
    document.removeEventListener('handlesubtitlesscreenposition', this.handleScreenPosition);
    this._wrapperRef = null;
    this._containerRef = null;
    this._lastSubId = null;
    this.subtitleArray = [];
  }

  // Búsqueda binaria O(log n) — reemplaza Array.filter() O(n) llamado 4-25x/segundo
  /*
      _findSubtitleAt(adjustedTime) {
          var arr = this.subtitleArray;
          if (!arr || arr.length === 0) return null;

          // Android muestra el subtítulo 250ms antes para compensar el lag de renderizado
          var androidOffset = this.isAndroid ? 250 : 0;
          var lookupTime = adjustedTime + androidOffset;

          var lo = 0;
          var hi = arr.length - 1;
          var candidate = -1;

          while (lo <= hi) {
              var mid = (lo + hi) >>> 1;
              if (arr[mid].start <= lookupTime) {
                  candidate = mid;
                  lo = mid + 1;
              } else {
                  hi = mid - 1;
              }
          }

          if (candidate >= 0 && arr[candidate].end >= adjustedTime) {
              return arr[candidate];
          }
          return null;
      }
  */

  _findSubtitleAt(time) {
    let low = 0; // Inicio del rango
    let high = this.subtitleArray.length - 1; // Fin del rango (ej. 1999)

    while (low <= high) {
      // Buscamos el punto medio
      let mid = Math.floor((low + high) / 2);
      let sub = this.subtitleArray[mid];

      // Caso 1: ¡Lo encontramos!
      if (time >= sub.start && time <= sub.end) {
        return sub;
      }
        // Caso 2: El tiempo que busco es MENOR al del subtítulo actual
      // Entonces descarto toda la mitad derecha del array
      else if (time < sub.start) {
        high = mid - 1;
      }
        // Caso 3: El tiempo que busco es MAYOR
      // Entonces descarto toda la mitad izquierda
      else {
        low = mid + 1;
      }
    }
    return null; // No hay subtítulo para ese segundo
  }


  handleChange(currentTime) {
    if (!currentTime || isNaN(currentTime) || currentTime <= 0) return;

    // Throttle
    const elapsed = currentTime - this._lastHandledTime;
    if (this._lastHandledTime >= 0 && Math.abs(elapsed) < this._throttleMs) return;
    this._lastHandledTime = currentTime;

    let adjustedTime = currentTime;
    if (this.flag) {
      adjustedTime = currentTime + (this.flag * 1000);
    }

    const subtitle = this._findSubtitleAt(adjustedTime);

    if (!this._containerRef) return;

    // --- REFACTORIZACIÓN CRÍTICA DEL DOM ---

    // Si no hay subtítulo y ya estaba vacío, no hacemos nada
    if (!subtitle) {
      // Cancelar rAF pendiente del idioma anterior para que no pinte contenido stale
      // en el DOM después del cambio de pista (independientemente del estado de _lastSubId).
      if (this._pendingRaf !== null) {
        window.cancelAnimationFrame(this._pendingRaf);
        this._pendingRaf = null;
      }
      if (this._lastSubId !== null) {
        this._containerRef.textContent = ''; // Usar textContent es más liviano
        this._containerRef.style.display = 'none'; // Ocultar evita cálculos de capas
        this._lastSubId = null;
      }
      return;
    }

    // Si el subtítulo es el mismo que ya estamos mostrando, BLOQUEAMOS la ejecución
    // Esto es lo que salvará a la PS4 de crashear
    if (this._lastSubId === subtitle.id || this._lastSubId === subtitle.start) {
      return;
    }

    // Solo llegamos aquí si el subtítulo REALMENTE cambió
    this._lastSubId = subtitle.id || subtitle.start;

    // Cancelar el frame pendiente anterior: si hay un rAF encolado de la pista
    // anterior (español/portugués), ejecutarlo ahora introduciría contenido stale
    // en el DOM, lo que en el WebKit del PS4 puede crashear el renderer.
    if (this._pendingRaf !== null) {
      window.cancelAnimationFrame(this._pendingRaf);
      this._pendingRaf = null;
    }

    // Usar requestAnimationFrame para sincronizar con el refresco de pantalla
    this._pendingRaf = window.requestAnimationFrame(() => {
      this._pendingRaf = null;
      if (this._containerRef) {
        // Usar DOM seguro en lugar de innerHTML para evitar el crash del HTML parser
        // del WebKit del PS4 cuando el nodo fue recién creado en el mismo event-loop tick.
        this._applySubtitleContent(this._containerRef, subtitle.part);
        // Aplicar estilos de posición uno a uno en lugar de style.cssText.
        // Los keys del objeto styles son nombres de propiedades JS DOM (camelCase),
        // no propiedades CSS (hyphenated). Asignarlos a style[key] es correcto;
        // pasarlos a style.cssText produce CSS inválido que puede crashear PS4 WebKit.
        this._applySubtitleStyles(this._containerRef.style, subtitle.styles);
        this._containerRef.style.display = 'block';
      }
    });
  }

  render() {
    if (!Array.isArray(this.subtitleArray) || this.subtitleArray.length === 0) {
      return null;
    }
    const className = 'subtitles ' + (this.props.className ? this.props.className : '');
    return (
      <div ref={this._setWrapperRef} className={className}>
        <div className="subtitles-container" ref={this._setContainerRef}/>
      </div>
    );
  }
}

export default VirtualSubtitles;



// WEBPACK FOOTER //
// ./src/components/Video/VirtualSubtitles.jsx